import { AxleChip } from '@/components/displayFields/chips';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { ExternalIdFragment } from '@/types/externalId';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Box,
    ChipProps,
    IconButton,
    IconButtonProps,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import { type MouseEvent, useState } from 'react';

const SCOPE_BADGE_SX = {
    bgcolor: 'grey.200',
    color: 'text.secondary',
    borderRadius: '4px',
    px: 0.75,
    fontSize: '0.6875rem',
    lineHeight: 1.5,
};

export const ExternalIdContentDisplay = ({
    externalId,
    externalLink,
    size,
}: {
    externalId: string | null;
    externalLink?: string | null;
    size?: ChipProps['size'];
}) => {
    const hasId = !!externalId;
    return (
        <HorizontalStack spacing={1} fullWidth={false}>
            <AxleChip
                label={hasId ? externalId : 'None'}
                copyToClipboardValue={externalId ?? undefined}
                link={externalLink ?? undefined}
                size={size}
            />
        </HorizontalStack>
    );
};

export const ExternalIdsDropdown = ({
    externalIds,
    popoverAnchorEl,
    popoverAlign = 'right',
    chipSize,
    ...iconButtonProps
}: {
    externalIds: ExternalIdFragment[];
    /** Element the popover is positioned against; defaults to the toggle button. */
    popoverAnchorEl?: HTMLElement | null;
    /** Which edge of the anchor the popover lines up with. */
    popoverAlign?: 'left' | 'right';
    chipSize?: ChipProps['size'];
} & Omit<IconButtonProps, 'onClick' | 'children'>) => {
    const [buttonEl, setButtonEl] = useState<HTMLElement | null>(null);

    const open = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setButtonEl(event.currentTarget);
    };

    return (
        <>
            <Tooltip title='Show all external IDs'>
                <IconButton
                    size='small'
                    aria-label='Show all external IDs'
                    {...iconButtonProps}
                    onClick={open}
                >
                    <ExpandMoreIcon fontSize='inherit' />
                </IconButton>
            </Tooltip>
            <Popover
                open={!!buttonEl}
                anchorEl={popoverAnchorEl ?? buttonEl}
                onClose={() => setButtonEl(null)}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: popoverAlign }}
                transformOrigin={{ vertical: 'top', horizontal: popoverAlign }}
            >
                <VerticalStack spacing={2} sx={{ p: 2 }}>
                    {externalIds.map((externalId) => (
                        <VerticalStack
                            key={`${externalId.system}-${externalId.type}-${externalId.value}`}
                        >
                            <HorizontalStack
                                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                                spacing={2}
                            >
                                <Typography variant='caption'>
                                    {externalId.type ?? externalId.system}
                                </Typography>
                                {externalId.scope ? (
                                    <Box sx={SCOPE_BADGE_SX}>{externalId.scope}</Box>
                                ) : null}
                            </HorizontalStack>
                            <ExternalIdContentDisplay
                                externalId={externalId.value}
                                size={chipSize}
                            />
                        </VerticalStack>
                    ))}
                </VerticalStack>
            </Popover>
        </>
    );
};

export const ExternalIdDisplay = ({
    externalId,
    externalLink,
    externalIds,
    label = true,
}: {
    externalId: string | null;
    externalLink?: string | null;
    externalIds: ExternalIdFragment[] | null;
    label?: boolean;
}) => {
    const singleId = externalIds && externalIds.length > 0 ? externalIds[0].value : externalId;
    const hasMultipleIds = !!externalIds && externalIds.length > 1;
    return (
        <VerticalStack>
            {label ? <Typography variant='caption'>External ID</Typography> : null}
            <HorizontalStack spacing={0.5}>
                <ExternalIdContentDisplay externalId={singleId} externalLink={externalLink} />
                {hasMultipleIds ? <ExternalIdsDropdown externalIds={externalIds} /> : null}
            </HorizontalStack>
        </VerticalStack>
    );
};
